import { useId } from "react";
import type { FieldValues, UseFormReturn } from "react-hook-form";
import {
  APAC_FORM_UI_NAME,
  ApacFormUiComponent,
  FormInputWrapper,
} from "@apac/form-ui";
import {
  type CoreIconNameEnum,
  useModuleBEM,
  useStyles,
} from "@b2bportal/core-themes";
import clsx from "clsx";
import { isRequiredLabel } from "../../utils/accessibleLabel";
import defaultStyles from "./styles.module.scss";

export interface TextFieldProps<T extends FieldValues> {
  formContext: UseFormReturn<T, unknown, undefined>;
  name: string;
  label?: string;
  className?: string;
  placeholder?: string;
  required?: boolean;
  defaultValue?: string;
  onLabelClick?: () => void;
  labelIconName?: CoreIconNameEnum;
  helperText?: React.ReactNode;
  disabled?: boolean;
  /** HTML autocomplete attribute for the underlying input element. */
  autoComplete?: string;
  /** HTML inputmode attribute, drives the virtual keyboard type on mobile. */
  inputMode?: React.ComponentProps<"input">["inputMode"];
}

export const TextField = <T extends FieldValues>(props: TextFieldProps<T>) => {
  const {
    formContext,
    className,
    placeholder = "",
    labelIconName,
    onLabelClick,
    disabled,
    label,
    required,
  } = props;
  const styles = useStyles(
    APAC_FORM_UI_NAME,
    ApacFormUiComponent.TextField,
    defaultStyles
  );
  const [block, cn] = useModuleBEM(styles, ApacFormUiComponent.TextField);

  // Associate the visible label with the input via htmlFor/id (WCAG 1.3.1 / 4.1.2).
  const inputId = useId();
  const ariaRequired = isRequiredLabel(label, required);

  return (
    <div className={clsx(block, className, disabled ? cn("disabled") : "")}>
      <FormInputWrapper
        {...props}
        htmlFor={inputId}
        labelIconName={labelIconName}
        onLabelClick={() => onLabelClick?.()}
        control={formContext.control}
        render={({ field, fieldState }) => {
          return (
            <input
              {...field}
              id={inputId}
              value={field.value as string}
              className={cn("input")}
              placeholder={placeholder}
              aria-required={ariaRequired || undefined}
              aria-invalid={fieldState.invalid || undefined}
              autoComplete={props.autoComplete}
              inputMode={props.inputMode}
            />
          );
        }}
      />
    </div>
  );
};
